import { useState } from 'react';
import {
  Download,
  File,
  FileArchive,
  FileCode,
  FileSpreadsheet,
  FileText,
  Film,
  FolderPlus,
  Image as ImageIcon,
  Loader2,
  Music,
  Presentation,
} from 'lucide-react';
import { api, ApiError, type ChatAttachmentRef } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { useChatAttachmentUrl } from '../hooks/useChatAttachmentUrl';
import { cn } from '@/lib/utils';
import { FileViewerModal } from './FileViewerModal';
import { useSaveDialogPrompt } from '@/components/files/file-dialog/useSaveDialogPrompt';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Chip glyph for a non-previewable attachment. Substring matching (rather than
 * an exact-MIME switch) keeps this working across the several spellings each
 * family has — e.g. `spreadsheetml.sheet`, `ms-excel` and `opendocument
 * .spreadsheet` are all spreadsheets.
 *
 * Order matters: the archive and spreadsheet checks come before the generic
 * `document`/`text` one, since `…officedocument.spreadsheetml.sheet` contains
 * "document" too.
 */
function FileTypeIcon({ mimeType, className }: { mimeType: string; className?: string }) {
  const mime = mimeType.toLowerCase();
  if (mime.startsWith('video/')) return <Film className={className} />;
  if (mime.startsWith('audio/')) return <Music className={className} />;
  if (mime.startsWith('image/')) return <ImageIcon className={className} />;
  if (
    mime.includes('zip') ||
    mime.includes('compressed') ||
    mime.includes('tar') ||
    mime.includes('gzip') ||
    mime.includes('bzip') ||
    mime.includes('-xz') ||
    mime.includes('rar')
  )
    return <FileArchive className={className} />;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv')
    return <FileSpreadsheet className={className} />;
  if (mime.includes('presentation') || mime.includes('powerpoint'))
    return <Presentation className={className} />;
  if (mime.includes('json') || mime.includes('xml') || mime.includes('yaml'))
    return <FileCode className={className} />;
  if (mime.includes('pdf') || mime.includes('document') || mime.includes('text'))
    return <FileText className={className} />;
  return <File className={className} />;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AttachmentItemProps {
  attachment: ChatAttachmentRef;
  /** True when shown inside the sender's own message bubble (green tint). */
  isOwnMessage?: boolean;
  /** Channel the message belongs to — needed to resolve channel-scoped (catt_) URLs. */
  channelId: string | null;
}

export function AttachmentItem({ attachment, isOwnMessage, channelId }: AttachmentItemProps) {
  // Inline URL — renders thumbnails and previews in-page instead of downloading.
  const url = useChatAttachmentUrl(channelId, attachment.fileId, 'inline');
  const [hovered, setHovered] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [savingToVault, setSavingToVault] = useState(false);
  const { promptSave, saveDialog } = useSaveDialogPrompt();

  const isImage = attachment.mimeType.startsWith('image/');
  const isVideo = attachment.mimeType.startsWith('video/');
  // Save-to-Fias-files only applies to channel-scoped attachments; legacy
  // `vdoc_` refs already live in the Vault.
  const isChannelAttachment = attachment.fileId.startsWith('catt_');

  // Click anywhere on the attachment opens the in-page viewer (which previews
  // images/video/audio/PDF/text and offers download / save for the rest).
  const handleOpen = () => {
    if (url) setViewerOpen(true);
  };

  const handleDownload = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      // The download attribute is ignored cross-origin, so force a save via an
      // attachment-disposition presigned URL (the inline one would render).
      const dlUrl =
        isChannelAttachment && channelId
          ? (await api.getChatAttachmentDownloadUrl(channelId, attachment.fileId, 'attachment')).url
          : url;
      if (!dlUrl) return;
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = attachment.fileName;
      a.rel = 'noopener noreferrer';
      a.click();
    } catch {
      toast({
        title: 'Download failed',
        description: 'Could not download the file.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveToVault = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!channelId || !isChannelAttachment || savingToVault) return;
    setSavingToVault(true);
    try {
      // The user picks the folder of their Documents; the route copies the
      // attachment there server-side. Name clashes are the dialog's to ask.
      const saved = await promptSave({
        suggestedName: attachment.fileName,
        mimeType: attachment.mimeType,
        saver: { kind: 'user', label: 'Chat', memoryKey: 'arche_chat' },
        writer: (save) => api.saveChatAttachmentToVault(channelId, attachment.fileId, save),
      });
      if (saved) toast({ description: `Saved “${saved.name}” to your Documents.` });
    } catch (err) {
      const description =
        err instanceof ApiError && err.code === 'STORAGE_QUOTA_EXCEEDED'
          ? 'Your Fias files storage is full.'
          : 'Could not save to your Fias files.';
      toast({ title: "Couldn't save", description, variant: 'destructive' });
    } finally {
      setSavingToVault(false);
    }
  };

  return (
    <>
      {saveDialog}
      <div className="mt-1.5">
        {/* Filename label */}
        <p
          className={cn(
            // The chat bubble is always a light color (see MessageItem), so the
            // label must be dark in every theme — `text-foreground` would go
            // white in dark mode and vanish against the bubble.
            'text-[11px] mb-0.5 truncate max-w-[220px] text-black/50',
          )}
        >
          {attachment.fileName}
        </p>

        {/* Preview area */}
        <div
          className="relative inline-block cursor-pointer rounded-lg overflow-hidden"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={handleOpen}
          title="Click to open"
        >
          {isImage && url ? (
            <img
              src={url}
              alt={attachment.fileName}
              // `text-black/80` is for the FAILED case, not the happy one: a
              // broken image renders its alt text in the INHERITED colour, and
              // `body` is `text-foreground` — pure white in dark mode — against
              // a bubble that is always light (see MessageItem). That is what
              // made an unloadable attachment read as an invisible filename.
              className="max-w-[220px] max-h-[160px] object-cover rounded-lg block text-[11px] text-black/80"
              draggable={false}
            />
          ) : isVideo && url ? (
            /* Video element with preload=metadata shows the first frame as thumbnail */
            <video
              src={url}
              preload="metadata"
              className="max-w-[220px] max-h-[160px] object-cover rounded-lg block"
              muted
            />
          ) : (
            /* Generic file chip */
            <div
              className={cn(
                // `text-black/80` forces dark text/icon in every theme — the
                // bubble behind is always light, so inheriting `text-foreground`
                // would render white-on-light in dark mode.
                'flex items-center gap-2 px-3 py-2 rounded-lg border min-w-[160px] text-black/80',
                isOwnMessage ? 'bg-black/10 border-black/15' : 'bg-black/[0.05] border-black/10',
              )}
            >
              <FileTypeIcon
                mimeType={attachment.mimeType}
                className="h-5 w-5 flex-shrink-0 opacity-60"
              />
              <div className="min-w-0">
                <p className="text-[12px] font-medium truncate max-w-[140px]">
                  {attachment.fileName}
                </p>
                <p className="text-[11px] opacity-50">{formatBytes(attachment.sizeBytes)}</p>
              </div>
            </div>
          )}

          {/* Action overlay — appears on hover */}
          {url && hovered && (
            <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1">
              {isChannelAttachment && (
                <button
                  type="button"
                  onClick={handleSaveToVault}
                  disabled={savingToVault}
                  title="Save to Fias files"
                  className="p-1 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors disabled:opacity-60"
                >
                  {savingToVault ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FolderPlus className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={handleDownload}
                title="Download"
                className="p-1 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Loading shimmer while URL resolves */}
          {!url && (isImage || isVideo) && (
            <div className="w-[180px] h-[120px] rounded-lg bg-black/10 animate-pulse" />
          )}
        </div>
      </div>

      {/* Full-screen in-page viewer (images, video, audio, PDF, text + fallback) */}
      {viewerOpen && url && (
        <FileViewerModal
          attachment={attachment}
          url={url}
          onClose={() => setViewerOpen(false)}
          onDownload={handleDownload}
          onSaveToVault={isChannelAttachment ? handleSaveToVault : undefined}
          savingToVault={savingToVault}
        />
      )}
    </>
  );
}
